import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, addMonths } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarIcon, FileText, Save, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from '@/components/ui/use-toast';
import { updateEmployee } from '@/services/employee.service';
import { Agreement } from '@/types/models';

// Agreement form schema
const agreementSchema = z.object({
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  periodInMonths: z.string().min(1, 'Period in months is required'),
  notifyPeriodInDays: z.string().min(1, 'Notify period in days is required'),
  note: z.string().optional(),
}).refine((data) => {
  if (data.startDate && data.endDate) {
    return new Date(data.endDate) > new Date(data.startDate);
  }
  return true;
}, {
  message: 'End date must be after start date',
  path: ['endDate'],
});

type AgreementFormValues = z.infer<typeof agreementSchema>;

interface EditAgreementDialogProps {
  agreement: Agreement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditAgreementDialog({
  agreement,
  open,
  onOpenChange,
  onSuccess,
}: EditAgreementDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AgreementFormValues>({
    resolver: zodResolver(agreementSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      periodInMonths: '',
      notifyPeriodInDays: '',
      note: '',
    },
  });

  // Automatically calculate end date when start date or period in months changes
  const watchedStartDate = form.watch('startDate');
  const watchedPeriod = form.watch('periodInMonths');

  useEffect(() => {
    if (watchedStartDate && watchedPeriod) {
      const start = new Date(watchedStartDate);
      const months = parseInt(watchedPeriod);

      if (!isNaN(start.getTime()) && !isNaN(months) && months > 0) {
        // Calculate end date based on duration (start date + months)
        const end = addMonths(start, months);
        const formattedEndDate = format(end, 'yyyy-MM-dd');

        // Only update if different to avoid infinite loop
        if (form.getValues('endDate') !== formattedEndDate) {
          form.setValue('endDate', formattedEndDate, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    }
  }, [watchedStartDate, watchedPeriod, form]);

  // Update form when agreement changes
  useEffect(() => {
    if (agreement && open) {
      // Convert dates to YYYY-MM-DD format for date inputs
      const formatDateForInput = (date: Date | string) => {
        if (!date) return '';
        try {
          const dateObj = typeof date === 'string' ? new Date(date) : date;
          // Check if date is valid
          if (isNaN(dateObj.getTime())) return '';
          return dateObj.toISOString().split('T')[0];
        } catch (error) {
          console.error('Date formatting error:', error);
          return '';
        }
      };

      const startDate = formatDateForInput(agreement.startDate);
      const endDate = formatDateForInput(agreement.endDate);

      form.reset({
        startDate,
        endDate,
        periodInMonths: agreement.periodInMonths || '',
        notifyPeriodInDays: agreement.notifyPeriodInDays || '',
        note: agreement.note || '',
      });
    }
  }, [agreement, open, form]);

  const onSubmit = async (data: AgreementFormValues) => {
    if (!agreement) return;

    setIsLoading(true);
    try {
      // Get the full employee data to access agreementInfo
      const { getEmployees } = await import("@/services/employee.service");
      const employees = await getEmployees();
      const employee = employees.find((emp: any) => emp._id === agreement.employee._id);

      if (!employee || !employee.agreementInfo) {
        toast({
          title: "Error",
          description: "Employee or agreement data not found",
          variant: "destructive",
        });
        return;
      }

      // Find the agreement record to update by matching start and end dates
      const agreementIndex = employee.agreementInfo.findIndex(
        (agr: any) =>
          new Date(agr.startDate).getTime() === new Date(agreement.startDate).getTime() &&
          new Date(agr.endDate).getTime() === new Date(agreement.endDate).getTime()
      );

      if (agreementIndex === -1) {
        toast({
          title: "Error",
          description: "Agreement record not found",
          variant: "destructive",
        });
        return;
      }

      // Update the agreement record
      const updatedAgreementInfo = [...employee.agreementInfo];
      updatedAgreementInfo[agreementIndex] = {
        ...updatedAgreementInfo[agreementIndex],
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        periodInMonths: data.periodInMonths,
        notifyPeriodInDays: data.notifyPeriodInDays,
        note: data.note || '',
      };

      // Update the employee using the same API as user profile
      await updateEmployee(employee._id, {
        agreementInfo: updatedAgreementInfo,
      });

      toast({
        title: "Success",
        description: "Agreement updated successfully",
        duration: 2000,
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update agreement";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" key={agreement?._id}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Edit Agreement
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                {agreement?.employee.user.firstName} {agreement?.employee.user.lastName}
              </p>
            </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Agreement Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Start Date
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <div className="relative cursor-pointer">
                                <Input
                                  readOnly
                                  value={field.value ? format(new Date(field.value), "dd/MM/yyyy") : ""}
                                  placeholder="DD/MM/YYYY"
                                  className="h-10 pr-10 cursor-pointer bg-white focus-visible:ring-1"
                                />
                                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                              </div>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={field.value ? new Date(field.value) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  field.onChange(format(date, "yyyy-MM-dd"));
                                }
                              }}
                              disabled={(date) =>
                                date < new Date("1900-01-01")
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm font-medium text-gray-700">
                          End Date
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <div className="relative cursor-pointer">
                                <Input
                                  readOnly
                                  value={field.value ? format(new Date(field.value), "dd/MM/yyyy") : ""}
                                  placeholder="DD/MM/YYYY"
                                  className="h-10 pr-10 cursor-pointer bg-white focus-visible:ring-1"
                                />
                                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                              </div>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={field.value ? new Date(field.value) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  field.onChange(format(date, "yyyy-MM-dd"));
                                }
                              }}
                              disabled={(date) =>
                                date < new Date("1900-01-01")
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="periodInMonths"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Period (In Months)
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter period in months"
                            {...field}
                            className="h-10"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notifyPeriodInDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Notify Period (In Days)
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter notify period in days"
                            {...field}
                            className="h-10"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="note"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Note
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter additional notes"
                          {...field}
                          className="h-10"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <DialogFooter className="gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}